import React, { useState, useEffect } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEntitlements } from '../contexts/EntitlementContext';
import api from '../services/api';
import { auditService } from '../services/auditService';
import {
    Home, Shield, FileText, AlertTriangle, Users,
    Settings, LogOut, ChevronDown, ChevronRight,
    BarChart2, Lock, Monitor, Briefcase,
    Layers, CheckSquare, HardDrive, Wifi, ShieldCheck, Upload,
    ArrowLeft, Archive, ClipboardCheck, BookOpen, Radar,
    Building, ClipboardCheck as Approve, Cable, X, Sparkles,
    ScrollText, Network, ShieldAlert, Inbox
} from 'lucide-react';

// Sidebar is fixed on lg+ (≥1024px). On smaller screens it's a slide-in
// drawer controlled by `mobileOpen` from the parent shell. `onClose`
// fires when the user taps the backdrop or any nav link.
const Sidebar = ({ mobileOpen = false, onClose = () => {} }) => {
    const { user, logout } = useAuth();
    const { hasFeature } = useEntitlements();
    const { tenantId } = useParams();
    const baseUrl = tenantId ? `/t/${tenantId}` : '';

    // Layout guard (Part 3): only show the CMMC Supply Chain ledger link in the
    // VRM nav when this tenant has CMMC Level 2 among its active frameworks.
    const [hasCmmc, setHasCmmc] = useState(false);
    useEffect(() => {
        let alive = true;
        api.get('/frameworks/').then((res) => {
            const fws = Array.isArray(res.data) ? res.data : (res.data?.frameworks || []);
            const on = fws.some((f) => {
                const code = String(f.code || f.framework_code || '').toUpperCase();
                const name = String(f.name || '').toUpperCase();
                return code.includes('CMMC') || name.includes('CMMC');
            });
            if (alive) setHasCmmc(on);
        }).catch(() => {});
        return () => { alive = false; };
    }, [tenantId]);

    // Detect if SuperAdmin is impersonating a tenant
   const isImpersonating = !!localStorage.getItem('admin_token') && user?.is_superuser;
    // Owner 2026-10-17: the banner shows the real mode. A support session in the superadmin's own
    // home tenant can write; any other tenant is read-only (the server decides: masquerade-info).
    const [supportReadOnly, setSupportReadOnly] = useState(true);
    useEffect(() => {
        if (!isImpersonating) return undefined;
        let alive = true;
        api.get('/auth/masquerade-info')
            .then((r) => { if (alive) setSupportReadOnly(r.data?.read_only !== false); })
            .catch(() => { if (alive) setSupportReadOnly(true); });
        return () => { alive = false; };
    }, [isImpersonating, tenantId]);
    const isAdmin = user?.role === 'admin' || user?.role === 'tenant_admin' || user?.role === 'org_admin' || user?.is_superuser || isImpersonating;

    // Read-only roles see most pages but with edit affordances hidden by
    // each component. The Sidebar additionally hides admin/management
    // entries that aren't safe for read-only access at all.
    const _role = ((user?.role || '') + '').trim().toLowerCase();
    const isViewer = !user?.is_superuser && _role === 'viewer';
    const isAuditor = !user?.is_superuser && _role === 'auditor';
    // Role model (owner 2026-10-10): internal auditors read everything and use Internal
    // Audit; external auditors stay on the Auditor Portal; employees see My tasks only.
    const isInternalAuditor = isAuditor && (user?.auditor_type || '') === 'internal';
    const isEmployee = !user?.is_superuser && _role === 'employee';
    const isReadOnly = isViewer || isAuditor || isEmployee;

    // Pending approval tasks count for badge
    const [pendingTaskCount, setPendingTaskCount] = useState(0);

    useEffect(() => {
        const fetchPendingCount = async () => {
            try {
                const res = await api.get('/vault/pending-tasks/count');
                setPendingTaskCount(res.data?.count || 0);
            } catch (err) {
                // Silently fail — badge just won't show
            }
        };
        if (tenantId) {
            fetchPendingCount();
            // Refresh every 60 seconds
            const interval = setInterval(fetchPendingCount, 60000);
            return () => clearInterval(interval);
        }
    }, [tenantId]);

    // Phase C — open Audit Requests (auditor evidence requests) badge count.
    const [auditReqCount, setAuditReqCount] = useState(0);
    useEffect(() => {
        const fetchAuditReqs = async () => {
            try {
                const open = await auditService.getEvidenceRequests(true);
                setAuditReqCount((open || []).length);
            } catch (err) {
                // Silently fail — badge just won't show
            }
        };
        if (tenantId && !isAuditor) {
            fetchAuditReqs();
            const interval = setInterval(fetchAuditReqs, 60000);
            return () => clearInterval(interval);
        }
    }, [tenantId, isAuditor]);

    // Group State (Open/Close) - Default all open.
    // VRM Deploy 3.3.2 — Governance now has nested sub-groups
    // (governance:risk + governance:vrm), each independently collapsible.
    const [groups, setGroups] = useState({
        compliance: true,
        document: true,
        governance: true,
        'governance:risk': true,
        'governance:vrm': true,
        performance: true,
        improvement: true,
        auditor: true,
        report: true,
        manage: true,
        mytasks: true
    });

    const toggleGroup = (key) => {
        setGroups(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleLogout = async () => {
        if (window.confirm("Are you sure you want to logout?")) {
            // Clear impersonation tokens on full logout
            localStorage.removeItem('admin_token');
            await logout();
            window.location.href = '/login';
        }
    };

    const handleReturnToConsole = async () => {
        const adminToken = localStorage.getItem('admin_token');
        if (adminToken) {
            // A1: end support access — writes the audit END row and restores the admin session.
            try { await api.post('/auth/impersonate/end'); } catch (e) { /* not in a session */ }
            localStorage.removeItem('admin_token');
            // Navigate back to Global Console
            window.location.href = '/super-admin';
        }
    };

    const navGroups = [
        {
            key: 'compliance',
            label: 'COMPLIANCE',
            items: [
                { label: 'Dashboard', path: `${baseUrl}/dashboard`, icon: Home },
                { label: 'Controls', path: `${baseUrl}/controls`, icon: Layers },
                { label: 'Statement of Applicability', path: `${baseUrl}/soa-preview`, icon: FileText },
                { label: 'Effectiveness', path: `${baseUrl}/effectiveness`, icon: BarChart2 },
                { label: 'Record an activity', path: `${baseUrl}/activities`, icon: CheckSquare },
                { label: 'Compliance Overview', path: `${baseUrl}/compliance-dashboard`, icon: ShieldCheck },
                ...((hasFeature('aws_scanner') || hasFeature('github_scanner')) ?
                    [{ label: 'Monitors', path: `${baseUrl}/monitors`, icon: Monitor }] : []),
                // Workstream 5 — Vanta-style automated tests view.
                // Uses CheckSquare icon (already imported above) so we
                // don't need a new icon dependency.
                { label: 'Tests', path: `${baseUrl}/tests`, icon: CheckSquare },
            ]
        },
        {
            // Deploy 2.70 — unified AUDIT LOG section. The three log surfaces
            // grouped together with purpose-clear names:
            //   Activity Logs          → in-tool user/platform actions (audit_logs)
            //   Connector Privacy Logs → cloud-scanner read-only proof (connector_logs)
            //   SIEM / Log Sources     → external SIEM / log-source verification
            key: 'auditlog',
            label: 'AUDIT LOG',
            items: [
                { label: 'Activity Logs', path: `${baseUrl}/audit-log`, icon: Lock },
                { label: 'Connector Privacy Logs', path: `${baseUrl}/connector-logs`, icon: Cable },
                { label: 'SIEM / Log Sources', path: `${baseUrl}/log-auditing`, icon: ScrollText },
                // Phase C — auditee inbox for auditor evidence requests.
                ...(!isAuditor ? [{ label: 'Audit Requests', path: `${baseUrl}/audit-requests`, icon: Inbox, badge: auditReqCount > 0 ? String(auditReqCount) : null }] : []),
            ]
        },
        {
            key: 'document',
            label: 'DOCUMENT',
            items: [
                // Angela is a paid add-on — hiding the item is UX only; the
                // real gate is server-side (service check + FeatureGuard).
                ...(hasFeature('angela') ? [{ label: 'Compliance Advisor', path: `${baseUrl}/copilot`, icon: Sparkles }] : []),
                { label: 'Security Questionnaires', path: `${baseUrl}/questionnaires`, icon: ClipboardCheck },
                { label: 'SME Queue', path: `${baseUrl}/questionnaires/sme-queue`, icon: Inbox },
                { label: 'Policy Library', path: `${baseUrl}/policies`, icon: FileText },
                { label: 'Document Vault', path: `${baseUrl}/vault`, icon: Archive },
                // Approval Tasks — admin/editor only (read-only roles can't approve)
                ...(!isReadOnly ? [{ label: 'Approval Tasks', path: `${baseUrl}/approval-tasks`, icon: ClipboardCheck, badge: pendingTaskCount > 0 ? String(pendingTaskCount) : null }] : []),
            ]
        },
        {
            // VRM Deploy 3.3.2 — Governance is now a two-tier nav.
            // GOVERNANCE is the top heading. Underneath are two
            // independently collapsible sub-groups: Risk Management
            // (everything that was here before) and Vendor Risk
            // Management (the VRM module). When more VRM features ship
            // (Discovery inbox, Evidence Expiry, Assessment queue,
            // Reminder dashboard), they go under the vrm sub-group.
            key: 'governance',
            label: 'GOVERNANCE',
            subGroups: [
                {
                    key: 'governance:risk',
                    label: 'Risk Management',
                    icon: Shield,
                    items: [
                        { label: 'Risk Hub', path: `${baseUrl}/risk`, icon: AlertTriangle },
                        { label: 'Risk Register', path: `${baseUrl}/risk/register`, icon: Shield },
                        { label: 'AI Systems', path: `${baseUrl}/risk/ai-systems`, icon: Sparkles },
                        { label: 'Asset Register', path: `${baseUrl}/risk/assets`, icon: HardDrive },
                        { label: 'Risk Library', path: `${baseUrl}/risk/library`, icon: BookOpen },
                        // Asset Discovery is a write-heavy scanner page — hide from read-only roles
                        ...(!isReadOnly ? [{ label: 'Asset Discovery', path: `${baseUrl}/risk/discovery`, icon: Radar }] : []),
                        // Approval Dashboard — admin/editor only
                        ...(!isReadOnly ? [{ label: 'Approval Dashboard', path: `${baseUrl}/risk/approval`, icon: Approve }] : []),
                        { label: 'Maturity Assessment', path: `${baseUrl}/risk/maturity`, icon: BarChart2 },
                        { label: 'Heatmap', path: `${baseUrl}/risk/heatmap`, icon: Layers },
                        { label: 'Methodology', path: `${baseUrl}/risk/methodology`, icon: FileText },
                        { label: 'Acceptance & treatment plan', path: `${baseUrl}/risk/acceptance-and-plan`, icon: FileText },
                        ...(isAdmin ? [
                            { label: 'Risk Profile', path: `${baseUrl}/risk/profile`, icon: Building },
                            { label: 'Risk Config', path: `${baseUrl}/risk/config`, icon: Settings },
                        ] : []),
                    ],
                },
                {
                    key: 'governance:vrm',
                    label: 'Vendor Risk Management',
                    icon: Briefcase,
                    items: [
                        // VRM Deploy 3.3 — Vendors list / detail / wizard /
                        // bulk import all under /vendors. Future VRM
                        // sub-items (Discovery inbox, Evidence Expiry,
                        // Assessments queue, Reminder dashboard) go here.
                        { label: 'Vendors', path: `${baseUrl}/vendors`, icon: Briefcase },
                        // VRM Deploy 3.4 — framework-aware methodology page
                        { label: 'Methodology', path: `${baseUrl}/vendors/methodology`, icon: FileText },
                        // Part 3 — CMMC Supply Chain Control Ledger, gated to
                        // tenants with CMMC Level 2 active.
                        ...(hasCmmc ? [{ label: 'CMMC Supply Chain', path: `${baseUrl}/vendors/cmmc-supply-chain`, icon: Network }] : []),
                    ],
                },
            ],
        },
        {
            key: 'privacy',
            label: 'PRIVACY (PIMS)',
            items: [
                { label: 'Records of Processing (RoPA)', path: `${baseUrl}/ropa`, icon: FileText },
                { label: 'PIA / DPIA Workspace', path: `${baseUrl}/pia`, icon: ShieldAlert },
                { label: 'Data Subject Requests', path: `${baseUrl}/dsr`, icon: Users },
                { label: 'Consent & Notices', path: `${baseUrl}/consent`, icon: ShieldCheck },
                { label: 'Vendor DPA Tracker', path: `${baseUrl}/vendor-dpa`, icon: Network },
            ]
        },
        {
            key: 'auditor',
            label: 'AUDITOR PORTAL',
            items: [
                { label: 'Auditor Portal', path: `${baseUrl}/auditor-portal`, icon: Shield },
                { label: 'Evidence Vault', path: `${baseUrl}/vault`, icon: Lock },
            ]
        },
        // PERFORMANCE EVALUATION (ISO/IEC 27001 clause 9). Tenant-facing section.
        // Internal Audit (9.2) + Management Review (9.3) MOVED here from the former
        // superadmin-only INTERNAL AUDIT group, but their superadmin gate travels
        // with them (route + server are still requireSuperuser / _superadmin_gate,
        // no tenant entitlement yet — Phase 2 ruling 2026-08-19), so they render
        // only for superadmins until that entitlement gate exists. The TISAX
        // Self-Assessment link is a SECOND path (module stays under its framework);
        // shown to all — the scoring screen degrades gracefully if TISAX is not
        // active. KPI Monitoring (9.1) is intentionally omitted until it has a
        // screen (the monitoring UI currently lives in the TISAX control drawer).
        {
            key: 'performance',
            label: 'PERFORMANCE EVALUATION',
            items: [
                // Owner ruling 2026-10-10: Internal Audit + Management Review for Admin and
                // Internal Auditor (role model).
                ...((isAdmin || isInternalAuditor) ? [
                    { label: 'Internal Audit', path: `${baseUrl}/internal-audit`, icon: ClipboardCheck },
                    { label: 'Audit Procedure', path: `${baseUrl}/internal-audit/procedure`, icon: FileText },
                    { label: 'Processes', path: `${baseUrl}/internal-audit/processes`, icon: Network },
                    { label: 'Management Review', path: `${baseUrl}/internal-audit/management-review`, icon: CheckSquare },
                ] : []),
                { label: 'TISAX Self-Assessment', path: `${baseUrl}/tisax-scoring`, icon: BarChart2 },
            ]
        },
        // IMPROVEMENT (ISO/IEC 27001 clause 10). NC Log (10.1) MOVED here from the
        // AUDITOR PORTAL group — nonconformity + corrective action is clause 10,
        // not a clause-9 evaluation activity. Route/path unchanged (/nc-log), so
        // existing bookmarks still resolve.
        {
            key: 'improvement',
            label: 'IMPROVEMENT',
            items: [
                { label: 'NC Log', path: `${baseUrl}/nc-log`, icon: AlertTriangle },
            ]
        },
        {
            key: 'report',
            label: 'REPORT',
            items: [
                { label: 'Reports Hub', path: `${baseUrl}/reports`, icon: BarChart2 },
                { label: 'CISO Dashboard', path: `${baseUrl}/reports/ciso-dashboard`, icon: Monitor },
            ]
        },
        {
            key: 'manage',
            label: 'MANAGE',
            items: [
                { label: 'People', path: `${baseUrl}/people`, icon: Users },
                { label: 'Identity Risks', path: `${baseUrl}/people/risks`, icon: AlertTriangle },
                { label: 'Groups', path: `${baseUrl}/groups`, icon: Users },
                { label: 'Computers', path: `${baseUrl}/computers`, icon: HardDrive },
                { label: 'Checklists', path: `${baseUrl}/checklists`, icon: CheckSquare },
                { label: 'Access', path: `${baseUrl}/access`, icon: Wifi },
                { label: 'Access reviews', path: `${baseUrl}/access-reviews`, icon: CheckSquare },
                // Integrations — admin/editor only (write-heavy connector setup)
                ...(!isReadOnly ? [{ label: 'Integrations', path: `${baseUrl}/integrations`, icon: Cable }] : []),
                ...(isAdmin ? [{ label: 'User Management', path: `${baseUrl}/admin/users`, icon: Users }] : []),
                ...(isAdmin ? [{ label: 'Settings', path: `${baseUrl}/settings`, icon: Settings }] : []),
                ...(isAdmin ? [{ label: 'Support access log', path: `${baseUrl}/support-access`, icon: Settings }] : []),
            ]
        }
    ];

    return (
        <>
            {/* Backdrop — only visible on small screens when drawer is open */}
            {mobileOpen && (
                <div
                    onClick={onClose}
                    className="lg:hidden fixed inset-0 bg-black/50 z-[49] transition-opacity"
                    aria-hidden="true"
                />
            )}

        <div
            className={[
                // Progressive sidebar width — keeps the same visual
                // proportion (~14-15%) across viewport sizes:
                //   < 1024  : w-64 (drawer)
                //   1024–1279 (small laptop)         : lg:w-52  = 208px
                //   1280–1535 (typical laptop, esp.  : xl:w-56  = 224px
                //              Windows 125% scaling)
                //   ≥ 1536 (desktop / 4K)            : 2xl:w-64 = 256px
                'w-64 lg:w-52 xl:w-56 2xl:w-64 bg-gradient-to-br from-sky-500 via-sky-200 to-sky-50 text-slate-800 h-screen flex flex-col fixed left-0 top-0 z-[50] overflow-y-auto border-r border-sky-100 font-sans text-sm',
                'transition-transform duration-200 ease-out',
                // On lg+ always visible; below lg slide based on mobileOpen.
                mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
            ].join(' ')}
            onClick={(e) => {
                // Auto-close drawer when a nav link is tapped on mobile.
                if (e.target.closest('a') && window.innerWidth < 1024) onClose();
            }}
        >
            {/* Mobile-only close button */}
            <button
                onClick={onClose}
                className="lg:hidden absolute top-3 right-3 text-slate-700 hover:text-slate-900 p-1.5 rounded-md hover:bg-white/40"
                aria-label="Close menu"
            >
                <X size={18} />
            </button>

            {/* Impersonation Banner */}
            {isImpersonating && (
                <div className="bg-amber-500 text-black px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Shield size={14} />
                        <span className="text-xs font-bold">
                            {supportReadOnly ? 'SUPPORT ACCESS · READ-ONLY' : 'SUPPORT ACCESS · OWN TENANT · FULL ACCESS'}
                        </span>
                    </div>
                    <button
                        onClick={handleReturnToConsole}
                        className="flex items-center gap-1 text-xs font-bold bg-black/20 hover:bg-black/30 px-2 py-1 rounded transition-colors"
                    >
                        <ArrowLeft size={12} />
                        Exit
                    </button>
                </div>
            )}

            {/* Header — combined brand logo (image + wordmark in one SVG) */}
            <div className="p-4 2xl:p-6">
                <div className="flex items-center select-none">
                    <img
                        src="/assurisk-logo.svg"
                        alt="AssuRisk"
                        className="h-10 2xl:h-12 w-auto"
                        draggable={false}
                    />
                </div>
            </div>

            {/* Navigation */}
            <div className="flex-1 px-4 space-y-6">
                {(isEmployee ? [{ key: 'mytasks', label: 'MY TASKS', items: [
                    { label: 'My tasks', path: `${baseUrl}/my-tasks`, icon: CheckSquare },
                ] }] : navGroups).map(group => (
                    <div key={group.key}>
                        <button
                            onClick={() => toggleGroup(group.key)}
                            className="flex items-center gap-2 w-full text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 hover:text-slate-900"
                        >
                            {groups[group.key] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            {group.label}
                        </button>

                        {groups[group.key] && (
                            <div className="space-y-2 ml-2 border-l border-sky-200 pl-2">
                                {/* VRM Deploy 3.3.2 — two render modes:
                                     1. `subGroups` → nested collapsible
                                        sub-headings (Risk Management,
                                        Vendor Risk Management).
                                     2. `items` (legacy) → flat list of
                                        NavLinks. */}
                                {Array.isArray(group.subGroups) && group.subGroups.length > 0 ? (
                                    group.subGroups.map(sub => (
                                        <div key={sub.key}>
                                            <button
                                                onClick={() => toggleGroup(sub.key)}
                                                className="flex items-center gap-1.5 w-full text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1 hover:text-slate-900 px-1 py-1"
                                            >
                                                {groups[sub.key] ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                                                {sub.icon && <sub.icon size={12} className="text-slate-500" />}
                                                <span className="text-left">{sub.label}</span>
                                            </button>
                                            {groups[sub.key] && (
                                                <div className="space-y-0.5 ml-3 border-l border-sky-100 pl-2">
                                                    {sub.items.map(item => (
                                                        <SidebarLink key={item.path} item={item} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    (group.items || []).map(item => (
                                        <SidebarLink key={item.path} item={item} />
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Footer / Profile */}
            <div className="p-4 bg-white/60 border-t border-sky-200">
                {/* Return to Console button (prominent, always visible when impersonating) */}
                {isImpersonating && (
                    <button
                        onClick={handleReturnToConsole}
                        className="w-full mb-3 flex items-center justify-center gap-2 py-2 px-3 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 hover:text-amber-300 rounded-lg text-xs font-bold transition-colors border border-amber-500/20"
                    >
                        <ArrowLeft size={14} />
                        Return to Global Console
                    </button>
                )}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${isImpersonating ? 'bg-gradient-to-tr from-amber-500 to-orange-500' : 'bg-gradient-to-tr from-blue-500 to-purple-500'}`}>
                            {user?.username?.substring(0, 2).toUpperCase() || 'AD'}
                        </div>
                        <div className="flex flex-col">
                            <span className="text-slate-900 text-xs font-medium">{user?.full_name || user?.username || 'Admin'}</span>
                            <span className="text-slate-600 text-[10px]">{isImpersonating ? 'SuperAdmin (Impersonating)' : 'Workspace Owner'}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <NavLink to={`${baseUrl}/account/security`} title="Security & Two-Factor Authentication"
                            className="text-slate-600 hover:text-slate-900 transition-colors">
                            <ShieldCheck size={16} />
                        </NavLink>
                        <button onClick={handleLogout} title="Logout" className="text-slate-600 hover:text-slate-900 transition-colors">
                            <LogOut size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

// VRM Deploy 3.3.2 — extracted to a small component so the new
// two-tier render (with subGroups) and the legacy flat render
// (with items) can share the exact same NavLink rendering.
const SidebarLink = ({ item }) => (
    <NavLink
        to={item.path}
        className={({ isActive }) =>
            `flex items-center justify-between px-3 py-2 rounded-md transition-all ${isActive
                ? 'bg-sky-700 text-white font-semibold shadow-sm'
                : 'text-slate-800 font-medium hover:text-slate-900 hover:bg-white/50'
            }`
        }
    >
        <div className="flex items-center gap-3">
            {item.icon && <item.icon size={16} />}
            <span>{item.label}</span>
        </div>
        {item.badge && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                /^\d+$/.test(item.badge)
                    ? 'bg-red-500 text-white min-w-[18px] text-center'
                    : 'bg-sky-200 text-slate-700'
            }`}>{item.badge}</span>
        )}
    </NavLink>
);

export default Sidebar;
